// The canvas half of a look (themes.md §3.4, §3.5, §12, R814, R823).
//
// These fields of the World body and of the Marks body name a mark the
// map DRAWS: the terrain, the shield bars, the group digits, the deposit mark,
// the Tender heal beam, the Tender shield aura, the four friendly marks of
// R185, the ship icon set, the fourteen hull fields of R835, the structure
// symbol of R1103, the three shot looks and three hit looks of R1007, the
// Dredger harvesting look of R1120, the anchoring look of R1125, the
// map marker look of R1291, the hold-fire look of R1439, the tube
// reload look of R1474, the four faction marks of R1568, the detonation
// look of R2079 and the seven looks of the Sapper part (R2652): the sea
// mine and the deployable mark, the sea mine detonation, and the lay and
// the collect looks.
// They reach the scene as one object, because the scene hands them on to
// three layers at once, and a layer that took its own field would let two of
// them disagree.
//
// The chrome fields are not here. A notices column, a dock-in and a banner
// are DOM, and `ui/chromelook.ts` carries those three.
//
// This is a leaf (`leaves.test.ts`): it holds the shape and the AUTO look
// and imports no engine. The generated id lists are its own data, and the
// typed unions of `ui/settings.ts` come in through a type-only clause, the
// way `chart-glyphs.ts` names the sinks of a layer: a type is erased before
// the bundler sees it, so the leaf still weighs nothing but itself.
//
// **The AUTO look is the shipped look.** Position 0 of every generated list
// is the AUTO value by construction, and balancegen asserts it (R822).

import {
  ANCHOR_STYLES, AURA_STYLES, BEAM_STYLES, DEPOSIT_STYLES, DIGIT_STYLES, GATHER_STYLES,
  FACTION_EDGE_STYLES, FACTION_HULL_STYLES, FACTION_KEY_STYLES, FACTION_SITE_STYLES,
  HULL_STYLES,
  DETONATION_STYLES,
  HIT_HULL_STYLES, HIT_SHIELD_STYLES, HIT_SINK_STYLES, HOLD_FIRE_STYLES, RELOAD_STYLES,
  MAP_MARKER_STYLES,
  OWN_FLEET_STYLES,
  OWN_HOLD_STYLES, SHIELD_STYLES, SHIP_STYLES,
  SHOT_ARTILLERY_STYLES, SHOT_MELEE_STYLES, SHOT_SENTINEL_STYLES,
  SQUAD_FLEET_STYLES,
  SQUAD_HOLD_STYLES,
  STRUCTURE_SYMBOLS,
  TERRAIN_STYLES,
  DEPLOYABLE_COLLECT_STYLES, DEPLOYABLE_LAY_STYLES, DEPLOYABLE_STYLES, SEA_MINE_COLLECT_STYLES,
  SEA_MINE_DETONATION_STYLES, SEA_MINE_LAY_STYLES, SEA_MINE_STYLES,
  CACHE_WORK_STYLES,
  CURRENT_STYLES,
  CURRENT_HEAT_STYLES,
  DEPTH_STYLES,
  POI_STYLES,
} from '../gen/balance';
import type { PoiSet } from './poi-marks';
import type {
  AnchorStyle, AuraStyle, BeamStyle, DepositStyle, DigitStyle, GatherStyle,
  HitHullStyle, HitShieldStyle,
  DetonationStyle,
  HitSinkStyle, HoldFireStyle, HullStyle, MapMarkerStyle, MarksHullField, ReloadStyle,
  OwnFleetStyle, OwnHoldStyle,
  ShieldStyle, ShipStyle, ShotArtilleryStyle, ShotMeleeStyle,
  ShotSentinelStyle, SquadFleetStyle, SquadHoldStyle, StructureSymbol,
  TerrainStyle,
} from '../ui/settings';

/** The four faction marks of R1568: the mark, or `off`. Position 0 is the
 *  mark, and it is the AUTO value. */
export type FactionSiteStyle = (typeof FACTION_SITE_STYLES)[number];
export type FactionHullStyle = (typeof FACTION_HULL_STYLES)[number];
export type FactionEdgeStyle = (typeof FACTION_EDGE_STYLES)[number];
export type FactionKeyStyle = (typeof FACTION_KEY_STYLES)[number];

/** The seven looks of the Sapper part (v2-the-deep.md §11.8, R2652). */
export type SeaMineStyle = (typeof SEA_MINE_STYLES)[number];
export type DeployableStyle = (typeof DEPLOYABLE_STYLES)[number];
export type SeaMineDetonationStyle = (typeof SEA_MINE_DETONATION_STYLES)[number];
export type SeaMineLayStyle = (typeof SEA_MINE_LAY_STYLES)[number];
export type DeployableLayStyle = (typeof DEPLOYABLE_LAY_STYLES)[number];
export type SeaMineCollectStyle = (typeof SEA_MINE_COLLECT_STYLES)[number];
export type DeployableCollectStyle = (typeof DEPLOYABLE_COLLECT_STYLES)[number];

/** The six cache work looks (R2833, `render/cache-work-looks.ts`). */
export type CacheWorkStyle = (typeof CACHE_WORK_STYLES)[number];

/** The five base looks of the current field (round 117, R2716, R2869). */
export type CurrentStyle = (typeof CURRENT_STYLES)[number];

/** The five current heat looks, with the speed field, that the CURRENTS
 *  heat map draws (R2869). */
export type CurrentHeatStyle = (typeof CURRENT_HEAT_STYLES)[number];

/** A look of either current row: what the chart's card 07 draws (R2869). */
export type CurrentLook = CurrentStyle | CurrentHeatStyle;

/** The two depth looks (round 116, R2726). */
export type DepthStyle = (typeof DEPTH_STYLES)[number];

/**
 * The fourteen hull fields of the Marks body, in roster order (R835).
 *
 * They all share one id list, so the three tables below build their twelve
 * entries from this one list rather than state them twelve times each.
 */
export const HULL_FIELDS = [
  'shipCorsair', 'shipCutlass', 'shipLancer', 'shipFalconet', 'shipBasilisk',
  'shipBombard', 'shipCommodore', 'shipVedette', 'shipDredger', 'shipSentinel',
  'shipBeacon', 'shipOracle', 'shipTender', 'shipSapper',
] as const satisfies readonly MarksHullField[];

/** What each of the fourteen hull fields holds. */
type HullFields = { [K in MarksHullField]: HullStyle };

/** Every hull field at one value, for a table that gives them all the same. */
function allHulls<T>(v: T): Record<MarksHullField, T> {
  const out = {} as Record<MarksHullField, T>;
  for (const f of HULL_FIELDS) out[f] = v;
  return out;
}

/** What the map scene takes from a look. */
export interface SceneLook extends HullFields {
  /** The ship icon set the whole roster wears (R835). */
  ship: ShipStyle;
  /** The ridges and the trenches (R100). */
  terrain: TerrainStyle;
  /** The current field (R2716). The chart furniture draws it
   *  (`current_arrows`, ui-map-interface.md §17.3.07). */
  currents: CurrentStyle;
  /** The current heat look (R2869): the look of the current field that
   *  the CURRENTS heat map draws, with the speed field. */
  currentsHeat: CurrentHeatStyle;
  /** The depth look (R2726). The depth pass of the depth tints draws it
   *  (`depth_tints`, ui-map-interface.md §17.3.09). */
  depth: DepthStyle;
  /** The shield and hull bars (R425). */
  shield: ShieldStyle;
  /** The control-group badge (R427). */
  digits: DigitStyle;
  /** The deposit mark (R475). */
  deposit: DepositStyle;
  /** The Tender heal beam (R725). */
  beam: BeamStyle;
  /** The Tender shield aura (R725). */
  aura: AuraStyle;
  /** The mark on an own hull (R185). */
  ownFleet: OwnFleetStyle;
  /** The mark on a squadmate hull (R185). */
  squadFleet: SquadFleetStyle;
  /** The mark on an own hold (R185). */
  ownHold: OwnHoldStyle;
  /** The mark on a squadmate hold (R185). */
  squadHold: SquadHoldStyle;
  /** The set all nine strategic objects are drawn as (R1103). */
  structure: StructureSymbol;
  /** The rounds one salvo flies, by the ROLE of the shooter (R1007). */
  shotMelee: ShotMeleeStyle;
  shotArtillery: ShotArtilleryStyle;
  shotSentinel: ShotSentinelStyle;
  /** What one round draws when it arrives (R1007): the shields hold, the
   *  shields are down, and the fleet goes down. */
  hitShield: HitShieldStyle;
  hitHull: HitHullStyle;
  hitSink: HitSinkStyle;
  /** What an own fleet draws around its hull while it holds fire (R1439). */
  holdFire: HoldFireStyle;
  /** What an own hull with tubes draws for its tube reload (R1474). */
  reload: ReloadStyle;
  /** What a round draws where it goes off, out to the splash of its kind
   *  (R2079). */
  detonation: DetonationStyle;
  /** What a Dredger draws while it cuts a deposit (R1120). */
  gather: GatherStyle;
  /** What a hull draws while its anchor goes down, holds and comes up
   *  (R1125). */
  anchoring: AnchorStyle;
  /** The look of a map marker (R1291). The chart furniture draws it
   *  (`map_markers`, ui-map-interface.md §17.3.66). */
  mapMarker: MapMarkerStyle;
  /** The faction second channel (R1568, ui-map-interface.md §2.2): the
   *  charge beside a hold and an installation of another faction, the pip
   *  astern of a hull of another faction, the dash of the territory edge,
   *  and the key of the five banners (`faction_key`, §17.3.67). */
  factionSite: FactionSiteStyle;
  factionHull: FactionHullStyle;
  factionEdge: FactionEdgeStyle;
  factionKey: FactionKeyStyle;
  /** The marks of the four sea mine kinds and of the six deployable kinds
   *  (R2652, `render/seamine-marks.ts`, `render/deployable-marks.ts`). */
  seaMine: SeaMineStyle;
  deployable: DeployableStyle;
  /** What a sea mine draws where it goes off (R2652,
   *  `render/seamine-detonation-looks.ts`). */
  seaMineDetonation: SeaMineDetonationStyle;
  /** What a Sapper draws while it lays and collects (R2652,
   *  `render/sapper-lay-looks.ts`, `render/sapper-collect-looks.ts`). */
  seaMineLay: SeaMineLayStyle;
  deployableLay: DeployableLayStyle;
  seaMineCollect: SeaMineCollectStyle;
  deployableCollect: DeployableCollectStyle;
  /** What an own fleet draws while it works a SEARCH (R2833,
   *  `render/cache-work-looks.ts`). */
  cacheWork: CacheWorkStyle;
  /** The set the five kinds of point of interest are drawn as (R2714,
   *  `render/poi-marks.ts`). The chart furniture draws it
   *  (`points_of_interest`, ui-map-interface.md §17.3.69). */
  poi: PoiSet;
}

/**
 * The ids each field may take, in the order of the two bodies. Position 0
 * of every list is the AUTO value (R822), which `AUTO_SCENE_LOOK` reads.
 */
const LISTS: { readonly [K in keyof SceneLook]: readonly SceneLook[K][] } = {
  terrain: TERRAIN_STYLES,
  currents: CURRENT_STYLES,
  currentsHeat: CURRENT_HEAT_STYLES,
  depth: DEPTH_STYLES,
  shield: SHIELD_STYLES,
  digits: DIGIT_STYLES,
  deposit: DEPOSIT_STYLES,
  beam: BEAM_STYLES,
  aura: AURA_STYLES,
  ownFleet: OWN_FLEET_STYLES,
  squadFleet: SQUAD_FLEET_STYLES,
  ownHold: OWN_HOLD_STYLES,
  squadHold: SQUAD_HOLD_STYLES,
  structure: STRUCTURE_SYMBOLS,
  shotMelee: SHOT_MELEE_STYLES,
  shotArtillery: SHOT_ARTILLERY_STYLES,
  shotSentinel: SHOT_SENTINEL_STYLES,
  hitShield: HIT_SHIELD_STYLES,
  hitHull: HIT_HULL_STYLES,
  hitSink: HIT_SINK_STYLES,
  holdFire: HOLD_FIRE_STYLES,
  reload: RELOAD_STYLES,
  detonation: DETONATION_STYLES,
  gather: GATHER_STYLES,
  anchoring: ANCHOR_STYLES,
  mapMarker: MAP_MARKER_STYLES,
  factionSite: FACTION_SITE_STYLES,
  factionHull: FACTION_HULL_STYLES,
  factionEdge: FACTION_EDGE_STYLES,
  factionKey: FACTION_KEY_STYLES,
  seaMine: SEA_MINE_STYLES,
  deployable: DEPLOYABLE_STYLES,
  seaMineDetonation: SEA_MINE_DETONATION_STYLES,
  seaMineLay: SEA_MINE_LAY_STYLES,
  deployableLay: DEPLOYABLE_LAY_STYLES,
  seaMineCollect: SEA_MINE_COLLECT_STYLES,
  deployableCollect: DEPLOYABLE_COLLECT_STYLES,
  cacheWork: CACHE_WORK_STYLES,
  poi: POI_STYLES,
  ship: SHIP_STYLES,
  ...allHulls<readonly HullStyle[]>(HULL_STYLES),
};

/**
 * The field names, for a caller that walks them, in the order of
 * `LISTS`. They are read off that table, whose type holds every field of
 * `SceneLook`, so a new field cannot be missed here.
 */
export const SCENE_FIELDS = Object.keys(LISTS) as readonly (keyof SceneLook)[];

/** Today's map: the AUTO value of every field, position 0 of its list. */
export const AUTO_SCENE_LOOK: SceneLook = autoLook();

function autoLook(): SceneLook {
  const out = {} as Record<string, string>;
  for (const f of SCENE_FIELDS) out[f] = LISTS[f][0];
  return out as unknown as SceneLook;
}

/**
 * A scene look read field by field, over AUTO.
 *
 * A field that is missing, or that names an id this build does not carry,
 * takes the AUTO value of that field — the rule the look resolver applies
 * to a custom body (§12). The adapter `ui/look.ts sceneLookOf` and the
 * theme preview (`preview/preview.ts applyShow`) both come through here.
 */
export function readSceneLook(src: Partial<Record<keyof SceneLook, string>>): SceneLook {
  const out = { ...AUTO_SCENE_LOOK };
  for (const f of SCENE_FIELDS) {
    const id = src[f];
    if (typeof id === 'string' && (LISTS[f] as readonly string[]).includes(id)) {
      (out as Record<string, string>)[f] = id;
    }
  }
  return out;
}
